import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {EnforcementObjectControllerEnforcementObjectsEndpoint} from '@/types/endpoint-contracts'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'

export function EnforcementObjects() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.enforcementObjects.pageTitle)
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: EnforcementObjectControllerEnforcementObjectsEndpoint,
        defaultSortBy: EnforcementObjectControllerEnforcementObjectsEndpoint.sort.DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const {enforcementTypes} = useAppConfig()

    const {data: enforcementData} = useQuery({
        queryKey: ['enforcement-objects', queryString, page, pageSize],
        queryFn: () => services.enforcementObjects.list(
            Object.fromEntries(new URLSearchParams(queryString))),
    })

    const enforcementObjects = enforcementData?.content || []
    const totalItems = enforcementData?.totalElements || 0
    const totalPages = enforcementData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.enforcementObject}
            title={i18n.enforcementObjects.allEnforcementObjects}
            count={totalItems}
            description={`${i18n.enforcementObjects.enforcementObjectCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="enforcement-objects-list"
            filters={
                <UnifiedSearchBar
                    contract={EnforcementObjectControllerEnforcementObjectsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.enforcementObjects.searchByName}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-external-id">{i18n.enforcementObjects.externalId}</TableHead>
                        <TableHead data-testid="col-system">{i18n.resources.system}</TableHead>
                        <TableHead data-testid="col-resource">{i18n.enforcementObjects.resource}</TableHead>
                        <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {enforcementObjects.map((obj) => (
                        <ClickableTableRow key={obj.id}
                                  to={routes.systems.enforcementObjects.detail(obj.id)}>
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.systems.enforcementObjects.detail(obj.id)}
                                    stopPropagation
                                    testId={`link-enforcement-object-${obj.id}`}
                                >
                                    {obj.derivedDisplayName}
                                </TextLink>
                                <MutedText size="sm" className="block">
                                    {obj.externalIdentifier}
                                </MutedText>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                                {obj.externalIdentifier}
                            </TableCell>
                            <TableCell>
                                <EntityLink
                                    value={obj.targetSystem}
                                    stopPropagation
                                    testId={`link-system-${obj.targetSystem.id}`}
                                />
                            </TableCell>
                            <TableCell>
                                {obj.resource ? (
                                    <EntityLink
                                        value={obj.resource}
                                        stopPropagation
                                        testId={`link-resource-${obj.resource.id}`}
                                    />
                                ) : (
                                    <span>&mdash;</span>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(enforcementTypes, obj.enforcementType)}
                                </Badge>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {enforcementObjects.length === 0 && (
                        <TableEmptyState
                            colSpan={5}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.enforcementObjects.noObjectsMatch}
                            emptyMessage={i18n.enforcementObjects.noObjectsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
